import { forwardRef } from "react";
import {
  RadioContext,
  type RadioGroupProps as RadioGroupPrimitiveProps,
} from "react-aria-components";
import { useDesignTokens } from "@boi/react-design-tokens";
import { type PropsWithOptionalTestID } from "@boi/components-core";

import * as S from "./RadioGroupV2.styled";
import { ReactAriaProviders } from "../ReactAriaProviders";
import { InputWrapper } from "../InputWrapper";
import { InputAlerts, type InputAlertsProps } from "../InputAlerts";
import { InputLabelTopV2, type InputLabelTopV2Props } from "../InputLabelTopV2";

export type RadioGroupV2Props = PropsWithOptionalTestID &
  Omit<RadioGroupPrimitiveProps, "orientation"> & {
    labelProps?: Omit<InputLabelTopV2Props, "children">;
    alerts?: InputAlertsProps["alerts"];
  };

const RadioGroupV2 = forwardRef<HTMLDivElement, RadioGroupV2Props>(
  (props, ref) => {
    const { labelProps, alerts, testID = "radio-group", ...otherProps } = props;

    const { tokens } = useDesignTokens();
    const { radioGroup: radioGroupTokens } = tokens;

    return (
      <ReactAriaProviders
        externallyLabelledBy={props["aria-labelledby"]}
        externallyDescribedBy={props["aria-describedby"]}
        labelProps={labelProps}
        alerts={alerts}
      >
        {({ labelledBy, describedBy }) => (
          <InputLabelTopV2 ref={ref} {...labelProps}>
            <InputWrapper
              gap={radioGroupTokens.gap}
              alert={alerts && <InputAlerts alerts={alerts} />}
            >
              <RadioContext.Provider
                value={{ "aria-describedby": describedBy }}
              >
                <S.RadioGroup
                  {...otherProps}
                  aria-labelledby={labelledBy}
                  aria-describedby={describedBy}
                  data-testid={testID}
                />
              </RadioContext.Provider>
            </InputWrapper>
          </InputLabelTopV2>
        )}
      </ReactAriaProviders>
    );
  }
);

RadioGroupV2.displayName = "RadioGroupV2";

export default RadioGroupV2;
